<template>

    <div class="header-wrapper txt-center">
      <div class="h1">{{ ordersPageTitle }}</div>
    </div>

    <div class="additional-option compact-table-controls">
      <div class="additional-option-row flex">
        <div class="additional-option-col flex">
          <button
              class="filters-toggle link-dotted-icon"
              type="button"
              :aria-expanded="filtersExpanded"
              aria-controls="order-list-filters"
              @click="toggleFilters"
          >
            <svg class="filters-toggle__icon" aria-hidden="true">
              <use :href="`/img/sprite.svg#${filtersExpanded ? 'svg-up' : 'svg-down'}`"></use>
            </svg>
            <span class="font-14">{{ t(filtersExpanded ? 'filters.hide' : 'filters.show') }}</span>
          </button>
        </div>
        <div class="additional-option-col flex">
          <a class="edit-option link-dotted-icon" @click="showSettings = true">
            <svg class="svg-edit">
              <use href="/img/sprite.svg#svg-settings"></use>
            </svg>
            <span class="font-14">{{ t('actions.columnSettings') }}</span>
          </a>
        </div>
      </div>
    </div>

    <div id="order-list-filters" v-show="filtersExpanded" class="filters-grid border-filters compact-filters">
      <template v-for="filter in filtersConfig" :key="filter.key">
        <MultiSelectFilter
            v-if="filter.type === 'multi-select'"
            v-model="filtersState[filter.stateKey]"
            :label="t(filter.labelKey)"
            :options="filter.options"
            :placeholder="t('form.select')"
        />

        <div v-else-if="filter.type === 'orders-checkboxes'" class="form-item refusal-checkbox span-1 orders-checkboxes">
          <label class="checkbox-label">
            <input
                type="checkbox"
                :checked="filtersState.showRefusalPositionsOnly"
                @change="filtersState.showRefusalPositionsOnly = $event.target.checked"
            >
            <span class="fake-checkbox"></span>
            <span class="checkbox-name">{{ t('filters.refusal') }}</span>
          </label>

          <label v-if="canViewCompletedOrders" class="checkbox-label">
            <input
                type="checkbox"
                :checked="isCompletedOrdersPage"
                @change="toggleCompletedOrders"
            >
            <span class="fake-checkbox"></span>
            <span class="checkbox-name">{{ t('filters.completedOrders') }}</span>
          </label>
        </div>

        <div v-else-if="filter.type === 'date'" class="form-item form-calendar span-1">
          <label class="calendar-label">{{ t(filter.labelKey) }}</label>

          <div class="calendar-field">
            <Datepicker
                v-model="orderDatePickerValue"
                :range="orderDateFilterMode === 'range'"
                class="calendar-input"
                :placeholder="t(filter.placeholderKey)"
                :enable-time-picker="false"
                auto-apply
                format="dd.MM.yyyy"
                :locale="locale"
            />
            <span class="icon-calendar"></span>
          </div>
        </div>

        <div v-else-if="filter.type === 'search'" class="filters-search span-2">
          <FieldSearchFilter
              v-model="filtersState[filter.stateKey]"
              :label="t(filter.labelKey)"
              :placeholder="t(filter.placeholderKey)"
              :fields="filter.fields"
          />
        </div>
      </template>
    </div>

    <div v-if="ordersLoading" class="table-group">
      <div class="table-loader"></div>
    </div>

    <div v-if="ordersError" class="mc-error">{{ ordersError }}</div>

    <div v-if="ordersLoaded" class="table-group" ref="tableGroup">

      <!-- Клон шапки для «прилипания» к верху окна -->
      <div class="table-sticky-header" v-if="stickyVisible" :style="stickyHeaderStyle">
        <div class="table-sticky-inner" :style="{ transform: `translateX(${-scrollLeft}px)` }">
          <table class="table order-table editable-table has-special-drop"
                 :style="{ width: stickyTableWidth + 'px' }">
            <colgroup>
              <col v-for="(w, i) in columnWidths" :key="i" :style="{ width: w + 'px' }" />
            </colgroup>
            <thead class="table-thead">
            <tr>
              <th
                  v-for="column in visibleTableColumns"
                  :key="column.key"
                  :class="column.class"
                  :title="column.labelKey ? t(column.labelKey) : null"
              >
                <template v-if="column.sortKey">
                  <button class="sort-btn" type="button" @click="setSort(column.sortKey)">
                    {{ t(column.labelKey) }}
                    <span class="sort-icons" aria-hidden="true">
                      <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                      <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
                    </span>
                  </button>
                </template>
              </th>
            </tr>
            </thead>
          </table>
        </div>
      </div>

      <button
          v-if="showScrollLeft"
          class="table-button-prev"
          type="button"
          :style="floatingButtonStyle"
          @mouseenter="startAutoScroll('left')"
          @mouseleave="stopAutoScroll"
          @mousedown.prevent="startAutoScroll('left')"
          @mouseup="stopAutoScroll"
          @touchstart.prevent="startAutoScroll('left')"
          @touchend="stopAutoScroll"
      >
        <svg class="svg-left">
          <use href="/img/sprite.svg#svg-left"></use>
        </svg>
      </button>

      <button
          v-if="showScrollRight"
          class="table-button-next"
          type="button"
          :style="floatingButtonStyle"
          @mouseenter="startAutoScroll('right')"
          @mouseleave="stopAutoScroll"
          @mousedown.prevent="startAutoScroll('right')"
          @mouseup="stopAutoScroll"
          @touchstart.prevent="startAutoScroll('right')"
          @touchend="stopAutoScroll"
      >
        <svg class="svg-right">
          <use href="/img/sprite.svg#svg-right"></use>
        </svg>
      </button>

      <div
          id="tableScroll"
          ref="tableScroll"
          class="table-wrapper scroll-x has-special-drop"
          @scroll="updateScrollButtons"
      >
        <table class="table order-table" :style="{width: `${orderTableWidth}px`}">
          <colgroup>
            <col
                v-for="column in visibleTableColumns"
                :key="column.key"
                :style="{width: `${column.width}px`}"
            >
          </colgroup>
          <thead class="table-thead">
          <tr>
            <th
                v-for="column in visibleTableColumns"
                :key="column.key"
                :class="column.class"
                :title="column.labelKey ? t(column.labelKey) : null"
            >
              <template v-if="column.sortKey">
                <button class="sort-btn" type="button" @click="setSort(column.sortKey)">
                  {{ t(column.labelKey) }}
                  <span class="sort-icons" aria-hidden="true">
                      <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                      <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
                    </span>
                </button>
              </template>
            </th>
          </tr>
          </thead>

          <tbody class="table-tbody">
          <tr
              v-for="(order, index) in filteredOrders"
              :key="order.xml_id || index"
              @dblclick="openOrderFromRow(order, $event)"
          >
            <td class="first-col">
              <button
                  v-if="order.has_refusal_positions"
                  class="btn-note has-tooltip has-tooltip-right"
                  type="button"
              >
                <span class="btn-tooltip">{{ t('tooltip.refusalPositions') }}</span>
              </button>
            </td>

            <td v-if="isColumnVisible('number')" class="cell-order-number" :title="displayText(order.number)">
                <span :class="order.color ? 'box-' + order.color : 'box-grey'">
                  {{ order.number }}
                </span>
            </td>

            <td v-if="isColumnVisible('date')" class="cell-date" :title="displayText(order.date)">{{ order.date }}</td>
            <td v-if="isColumnVisible('supplier_number')" class="cell-supplier-number" :title="displayText(order.supplier_number)">
              <input
                  v-if="canEditOrderIdentifiers"
                  class="edit-inp order-identifier-input"
                  type="text"
                  :title="getOrderFieldDraft(order, 'supplier_number')"
                  :value="getOrderFieldDraft(order, 'supplier_number')"
                  :disabled="isOrderFieldSaving(order, 'supplier_number')"
                  @input="setOrderFieldDraft(order, 'supplier_number', $event.target.value)"
                  @blur="saveOrderField(order, 'supplier_number')"
                  @keydown.enter.prevent="$event.target.blur()"
                  @keydown.esc.prevent="cancelOrderFieldEdit(order, 'supplier_number', $event)"
                  @dblclick.stop
              >
              <template v-else>{{ order.supplier_number }}</template>
            </td>
            <td v-if="isColumnVisible('invois')" class="cell-invoice" :title="displayText(order.invois)">
              <input
                  v-if="canEditOrderIdentifiers"
                  class="edit-inp order-identifier-input"
                  type="text"
                  :title="getOrderFieldDraft(order, 'invois')"
                  :value="getOrderFieldDraft(order, 'invois')"
                  :disabled="isOrderFieldSaving(order, 'invois')"
                  @input="setOrderFieldDraft(order, 'invois', $event.target.value)"
                  @blur="saveOrderField(order, 'invois')"
                  @keydown.enter.prevent="$event.target.blur()"
                  @keydown.esc.prevent="cancelOrderFieldEdit(order, 'invois', $event)"
                  @dblclick.stop
              >
              <template v-else>{{ order.invois }}</template>
            </td>
            <td v-if="isColumnVisible('invois_date')" class="cell-invoice-date" :title="displayText(order.invois_date)">{{ order.invois_date }}</td>
            <td v-if="isColumnVisible('update_date')" class="cell-edit" :title="displayText(order.update_date)">{{ order.update_date }}</td>
            <td v-if="isColumnVisible('filial_name')" class="cell-company" :title="displayEntityText(order.filial_name, order.filial_name_en)">{{ displayEntityText(order.filial_name, order.filial_name_en) }}</td>
            <td v-if="isColumnVisible('partner_name')" class="cell-contractor" :title="displayText(order.partner_name)">{{ order.partner_name }}</td>
            <td v-if="isColumnVisible('user_name')" class="cell-manager" :title="displayText(managerName(order))">{{ displayText(managerName(order)) }}</td>

            <td v-if="isColumnVisible('summa')" class="cell-price cell-price-amount" :title="displayText(order.summa)">
              <div class="price-drop">
                <span class="price-value">{{ order.summa ?? '-' }}</span>
              </div>
            </td>

            <td v-if="isColumnVisible('summa_work')" class="cell-price cell-deficit" :title="displayText(order.summa_work)">
              <div class="price-drop">
                <span class="price-value">{{ order.summa_work ?? '-' }}</span>
              </div>
            </td>

            <td v-if="isColumnVisible('status')" class="cell-status" :title="statusLabel(order.status)">{{ statusLabel(order.status) }}</td>

            <td class="cell-options">
              <router-link :to="getOrderRoute(order)" class="btn-view has-tooltip">
                <span class="btn-tooltip">{{ t('tooltip.view') }}</span>
              </router-link>
            </td>

            <td class="cell-options">
              <router-link v-if="(user.role=='manager' || user.role=='admin') && isDraftOrder(order.status)" :to="getOrderRoute(order)" class="btn-edit has-tooltip">
                <span class="btn-tooltip">{{ t('tooltip.edit') }}</span>
              </router-link>
            </td>

            <td class="cell-options">
              <button
                  v-if="canFinishOrder(order)"
                  class="btn-finish has-tooltip"
                  type="button"
                  @click="openFinishConfirm(order)"
              >
                <span class="btn-tooltip">{{ t('tooltip.finish') }}</span>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Settings
        :isOpen="showSettings"
        :items="settingsColumns"
        :selected="selectedColumnKeys"
        :is-submitting="isSavingColumnSettings"
        @close="showSettings = false"
        @apply="applyColumnSettings"
    />

    <div class="modal-frame" :class="{active: showFinishConfirm}">
      <div class="modal-frame-content">
        <button class="btn-close" rel="button_close" type="button" @click="closeFinishConfirm">
          <svg class="svg-cross">
            <use href="/img/sprite.svg#svg-cross"></use>
          </svg>
        </button>
        <div class="modal-frame-header">
          <div class="h3">{{ t('modals.confirmFinish') }}</div>
        </div>
        <div class="modal-footer font-18">
          <p class="txt-center">{{ t('modals.finishOrderQuestion') }}</p>
        </div>
        <div class="flex-center">
          <button class="btn" type="button" :class="{loader:isSubmitting === 'finishListOrder'}" @click="finishSelectedOrder">
            {{ t('modals.yes') }}
          </button>
          <button class="btn-outline" type="button" @click="closeFinishConfirm">{{ t('modals.no') }}</button>
        </div>
      </div>
    </div>

</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import MultiSelectFilter from '@/components/filters/MultiSelectFilter.vue';
import FieldSearchFilter from '@/components/filters/FieldSearchFilter.vue';
import Settings from '@/components/order/Settings.vue';

import { isDateInRange, getDateRangeFromPicker } from '@/utils/dateUtils';
import { sortArray, getSortValue } from '@/utils/sortUtils';
import { isOrderStatus, ORDER_STATUS_VALUES, translateOrderStatus } from '@/utils/statusUtils';
import { translateApiError } from '@/utils/apiErrorUtils';
import { localizedDisplayValue, localizedEntityValue } from '@/utils/transliteration';
import { sortOptionsByLabel } from '@/utils/optionSortUtils';
import { readFiltersExpanded, writeFiltersExpanded } from '@/utils/filterExpansion';
import scrollManagerMixin from '@/mixin/scrollManagerMixin';
import axios from 'axios';

const ORDER_LIST_ROUTES = ['main', 'orders-completed'];
const ORDER_FILTERS_STORAGE_PREFIX = 'sumiteclk:order-list-filters';
const EDITABLE_ORDER_FIELDS = ['supplier_number', 'invois'];
const ORDER_LIST_SETTINGS_KEY = 'order_list';
const FILTERS_EXPANDED_STORAGE_KEY = 'sumiteclk:filters-expanded:order-list';
const FIXED_ORDER_COLUMN_KEYS = new Set(['first-col', 'view', 'edit', 'finish']);

const createDefaultFiltersState = () => ({
  selectedStatuses: [],
  selectedSuppliers: [],
  selectedManagers: [],
  selectedOffices: [],
  showRefusalPositionsOnly: false,
  invoiceOrderSearch: '',
});

export default {
  name: 'Main',
  components: {
    Datepicker,
    MultiSelectFilter,
    FieldSearchFilter,
    Settings,
  },
  mixins: [scrollManagerMixin],
  setup() {
    const { t, locale } = useI18n();

    const changeLanguage = (lang) => {
      locale.value = lang;
      localStorage.setItem('locale', lang);
    };

    return {
      t,
      locale,
      changeLanguage
    };
  },
  data: () => ({
    currentLang: 'ru',

    sortKey: 'number',
    sortDir: 'asc',

    filtersState: createDefaultFiltersState(),

    orderDateFilterMode: 'range',
    orderDate: null,
    orderDateRange: null,
    showFinishConfirm: false,
    finishOrderTarget: null,
    isSubmitting: null,
    serverError: null,
    orderFieldEdits: {},
    savingOrderFields: {},
    showSettings: false,
    isSavingColumnSettings: false,
    filtersExpanded: false,
  }),

  created() {
    this.filtersExpanded = readFiltersExpanded(FILTERS_EXPANDED_STORAGE_KEY);
    this.restoreFiltersState(this.$route.name);
    this.loadOrders();
  },

  watch: {
    '$route.name'(newRouteName, oldRouteName) {
      this.saveFiltersState(oldRouteName);

      if (!ORDER_LIST_ROUTES.includes(newRouteName)) {
        return;
      }

      this.restoreFiltersState(newRouteName);
      this.loadOrders();
    },
    filtersState: {
      handler() {
        this.saveFiltersState();
      },
      deep: true,
    },
    orderDateFilterMode() {
      this.saveFiltersState();
    },
    orderDate() {
      this.saveFiltersState();
    },
    orderDateRange: {
      handler() {
        this.saveFiltersState();
      },
      deep: true,
    },
    orders: {
      handler(orders) {
        this.syncOrderFieldEdits(orders);
      },
      immediate: true,
    },
    ordersLoaded(val) {
      if (val) {
        this.updateScrollOnDataChange();
      }
    },
    filteredOrders() {
      this.updateScrollOnDataChange();
    },
  },

  computed: {
    ...mapGetters('orders', [
      'orders',
      'ordersLoading',
      'ordersError',
      'ordersLoaded',
    ]),
    ...mapGetters( [
      'user',
    ]),
    ...mapGetters('app', [
      'columnSettings',
      'appError',
    ]),
    isCompletedOrdersPage() {
      return this.$route.name === 'orders-completed';
    },
    canViewCompletedOrders() {
      return this.user?.role === 'manager' || this.user?.role === 'admin';
    },
    canEditOrderIdentifiers() {
      return this.user?.role === 'manager' || this.user?.role === 'admin';
    },
    ordersPageTitle() {
      return this.isCompletedOrdersPage ? this.t('pageTitleCompletedOrders') : this.t('pageTitle');
    },
    filtersConfig() {
      return [
        {
          key: 'status',
          type: 'multi-select',
          labelKey: 'filters.status',
          stateKey: 'selectedStatuses',
          options: this.statusOptions,
        },
        {
          key: 'supplier',
          type: 'multi-select',
          labelKey: 'filters.supplier',
          stateKey: 'selectedSuppliers',
          options: this.supplierOptions,
        },
        {
          key: 'manager',
          type: 'multi-select',
          labelKey: 'filters.manager',
          stateKey: 'selectedManagers',
          options: this.managerOptions,
        },
        {
          key: 'order-date',
          type: 'date',
          labelKey: 'filters.orderDate',
          placeholderKey: 'filters.choose',
        },
        {
          key: 'office',
          type: 'multi-select',
          labelKey: 'filters.office',
          stateKey: 'selectedOffices',
          options: this.officeOptions,
        },
        {
          key: 'refusal',
          type: 'orders-checkboxes',
        },
        {
          key: 'search',
          type: 'search',
          labelKey: 'filters.search',
          placeholderKey: 'filters.searchorder',
          stateKey: 'invoiceOrderSearch',
          fields: ['invois', 'number', 'supplier_number'],
        },
      ];
    },

    tableColumns() {
      return [
        { key: 'first-col', class: 'first-col', width: 40 },
        { key: 'number', class: 'cell-order-number', width: 180, sortKey: 'number', labelKey: 'table.number' },
        { key: 'date', class: 'cell-date', width: 105, sortKey: 'date', labelKey: 'table.date' },
        { key: 'supplier_number', class: 'cell-supplier-number', width: 190, sortKey: 'supplier_number', labelKey: 'orderTable.supplier_number' },
        { key: 'invois', class: 'cell-invoice', width: 190, sortKey: 'invois', labelKey: 'table.invois' },
        { key: 'invois_date', class: 'cell-invoice-date', width: 115, sortKey: 'invois_date', labelKey: 'table.invoisDate' },
        { key: 'update_date', class: 'cell-edit', width: 145, sortKey: 'update_date', labelKey: 'table.updateDate' },
        { key: 'filial_name', class: 'cell-company', width: 260, sortKey: 'filial_name', labelKey: 'table.company' },
        { key: 'partner_name', class: 'cell-contractor', width: 300, sortKey: 'partner_name', labelKey: 'table.supplier' },
        { key: 'user_name', class: 'cell-manager', width: 180, sortKey: 'user_name', labelKey: 'table.manager' },
        { key: 'summa', class: 'cell-price', width: 140, sortKey: 'summa', labelKey: 'table.amount' },
        { key: 'summa_work', class: 'cell-deficit', width: 250, sortKey: 'summa_work', labelKey: 'table.deficit' },
        { key: 'status', class: 'cell-status', width: 180, sortKey: 'status', labelKey: 'table.status' },
        { key: 'view', class: 'cell-options', width: 48 },
        { key: 'edit', class: 'cell-options', width: 48 },
        { key: 'finish', class: 'cell-options', width: 48 },
      ];
    },
    configurableTableColumns() {
      return this.tableColumns.filter(column => !FIXED_ORDER_COLUMN_KEYS.has(column.key));
    },
    visibleTableColumns() {
      return this.tableColumns.filter(column => (
          FIXED_ORDER_COLUMN_KEYS.has(column.key) || this.isColumnSelected(column.key)
      ));
    },
    orderTableWidth() {
      return this.visibleTableColumns.reduce((width, column) => width + column.width, 0);
    },
    settingsColumns() {
      return this.configurableTableColumns.map(column => ({
        key: column.key,
        label: this.t(column.labelKey),
      }));
    },
    selectedColumnKeys() {
      return this.configurableTableColumns
          .filter(column => this.isColumnSelected(column.key))
          .map(column => column.key);
    },
    normalizedColumnSettings() {
      return this.normalizeColumnSettings(this.columnSettings);
    },

    orderDatePickerValue: {
      get() {
        return this.orderDateFilterMode === 'range' ? this.orderDateRange : this.orderDate;
      },
      set(value) {
        if (this.orderDateFilterMode === 'range') {
          this.orderDateRange = value;
          if (Array.isArray(value) && value.length === 2 && !value[0] && !value[1]) {
            this.orderDateRange = null;
          }
        } else {
          this.orderDate = value;
          if (!value) {
            this.orderDate = null;
          }
        }
      },
    },

    sortedOrders() {
      return sortArray(this.orders || [], this.sortKey, this.sortDir);
    },

    statusOptions() {
      return this.buildUniqueOptions(this.orders, order => order?.status, status => this.statusLabel(status));
    },

    supplierOptions() {
      const options = this.buildUniqueOptions(
          this.orders,
          order => order?.partner_name,
          (value, order) => this.displayEntityText(value, order?.partner_name_en)
      );

      return sortOptionsByLabel(options, this.displayLocale());
    },

    managerOptions() {
      const options = this.buildUniqueOptions(
          this.orders,
          order => this.managerName(order),
          value => this.displayText(value)
      );

      return sortOptionsByLabel(options, this.displayLocale());
    },

    officeOptions() {
      return this.buildUniqueOptions(
          this.orders,
          order => order?.filial_name,
          (value, order) => this.displayEntityText(value, order?.filial_name_en)
      );
    },

    filteredOrders() {
      const search = this.normalizeSearch(this.filtersState.invoiceOrderSearch);
      const dateRange = getDateRangeFromPicker(this.orderDatePickerValue, this.orderDateFilterMode);

      return this.sortedOrders.filter(order => {
        const matchStatus = !this.filtersState.selectedStatuses.length || this.filtersState.selectedStatuses.includes(order.status);
        const matchSupplier = !this.filtersState.selectedSuppliers.length || this.filtersState.selectedSuppliers.includes(order.partner_name);
        const managerValue = `${order?.user_last_name || ''} ${order?.user_name || ''}`.trim();
        const matchManager = !this.filtersState.selectedManagers.length || this.filtersState.selectedManagers.includes(managerValue);
        const matchOffice = !this.filtersState.selectedOffices.length || this.filtersState.selectedOffices.includes(order.filial_name);
        const matchRefusalPositions = !this.filtersState.showRefusalPositionsOnly || Boolean(order?.has_refusal_positions);

        const matchInvoiceOrder =
            !search ||
            ['invois', 'number', 'supplier_number'].some(field => this.normalizeSearch(order?.[field]).includes(search));

        const matchDate = isDateInRange(order?.date, dateRange);

        return matchStatus && matchSupplier && matchManager && matchOffice && matchRefusalPositions && matchInvoiceOrder && matchDate;
      });
    },
  },

  methods: {
    ...mapActions('orders', [
      'fetchOrders',
    ]),
    ...mapActions('notify', [
      'addNotify',
    ]),

    loadOrders() {
      return this.fetchOrders({
        completed: this.isCompletedOrdersPage,
      });
    },

    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
      writeFiltersExpanded(FILTERS_EXPANDED_STORAGE_KEY, this.filtersExpanded);
      this.$nextTick(() => this.updateScrollOnDataChange());
    },

    normalizeColumnSettings(settings) {
      if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {};
      }

      const normalized = {};
      Object.entries(settings).forEach(([section, columns]) => {
        if (!columns || typeof columns !== 'object' || Array.isArray(columns)) {
          return;
        }

        normalized[section] = {};
        Object.entries(columns).forEach(([key, value]) => {
          normalized[section][key] = Boolean(value);
        });
      });

      return normalized;
    },

    isColumnSelected(key) {
      const settings = this.normalizedColumnSettings[ORDER_LIST_SETTINGS_KEY];

      if (!settings || !Object.prototype.hasOwnProperty.call(settings, key)) {
        return true;
      }

      return settings[key];
    },

    isColumnVisible(key) {
      return this.configurableTableColumns.some(column => column.key === key)
          && this.isColumnSelected(key);
    },

    async applyColumnSettings(selectedKeys) {
      if (this.isSavingColumnSettings) {
        return;
      }

      const allowedKeys = new Set(this.configurableTableColumns.map(column => column.key));
      const requestedKeys = Array.isArray(selectedKeys) ? selectedKeys : [];
      const selectedSet = new Set(requestedKeys.filter(key => allowedKeys.has(key)));

      if (!selectedSet.size) {
        return;
      }

      const currentSettings = this.normalizeColumnSettings(this.columnSettings);
      currentSettings[ORDER_LIST_SETTINGS_KEY] = {};
      this.configurableTableColumns.forEach(column => {
        currentSettings[ORDER_LIST_SETTINGS_KEY][column.key] = selectedSet.has(column.key);
      });

      this.isSavingColumnSettings = true;

      try {
        const response = await axios.post('/api/app/settings/', {column_settings: currentSettings}, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data?.status === 'error') {
          throw new Error('Column settings save error');
        }

        await this.$store.dispatch('app/fetchApp');
        if (this.appError) {
          throw new Error('Column settings reload error');
        }

        if (!selectedSet.has(this.sortKey)) {
          const fallbackColumn = this.configurableTableColumns.find(column => (
              column.sortKey && selectedSet.has(column.key)
          ));
          if (fallbackColumn) {
            this.sortKey = fallbackColumn.sortKey;
            this.sortDir = 'asc';
          }
        }

        this.showSettings = false;
        this.$nextTick(() => this.updateScrollOnDataChange());
      } catch (error) {
        console.error('Ошибка при сохранении настроек колонок списка заказов:', error);
        this.addNotify({text: this.t('notifications.columnSettingsError'), error: true});
      } finally {
        this.isSavingColumnSettings = false;
      }
    },

    getOrderRoute(order) {
      return {
        name: 'order',
        params: { id: order.id },
      };
    },

    openOrderFromRow(order, event) {
      const interactiveSelector = 'a, button, input, select, textarea, [role="button"], [contenteditable="true"]';

      if (event.button !== 0 || event.target?.closest?.(interactiveSelector)) {
        return;
      }

      this.$router.push(this.getOrderRoute(order));
    },

    getOrderFieldKey(order, field) {
      return `${order?.id}:${field}`;
    },

    getOrderFieldEdit(order, field) {
      const key = this.getOrderFieldKey(order, field);
      if (!this.orderFieldEdits[key]) {
        const value = String(order?.[field] ?? '');
        this.orderFieldEdits[key] = {
          draft: value,
          confirmed: value,
        };
      }

      return this.orderFieldEdits[key];
    },

    getOrderFieldDraft(order, field) {
      const edit = this.orderFieldEdits[this.getOrderFieldKey(order, field)];
      return edit ? edit.draft : String(order?.[field] ?? '');
    },

    setOrderFieldDraft(order, field, value) {
      this.getOrderFieldEdit(order, field).draft = value;
    },

    isOrderFieldSaving(order, field) {
      return Boolean(this.savingOrderFields[this.getOrderFieldKey(order, field)]);
    },

    syncOrderFieldEdits(orders) {
      const nextEdits = {};
      const nextSaving = {};

      (Array.isArray(orders) ? orders : []).forEach(order => {
        EDITABLE_ORDER_FIELDS.forEach(field => {
          const key = this.getOrderFieldKey(order, field);
          const value = String(order?.[field] ?? '');
          nextEdits[key] = {
            draft: value,
            confirmed: value,
          };
          if (this.savingOrderFields[key]) {
            nextSaving[key] = true;
          }
        });
      });

      this.orderFieldEdits = nextEdits;
      this.savingOrderFields = nextSaving;
    },

    cancelOrderFieldEdit(order, field, event) {
      const edit = this.getOrderFieldEdit(order, field);
      edit.draft = edit.confirmed;
      event?.target?.blur();
    },

    async saveOrderField(order, field) {
      const key = this.getOrderFieldKey(order, field);
      if (!order?.id || !EDITABLE_ORDER_FIELDS.includes(field) || this.savingOrderFields[key]) {
        return;
      }

      const edit = this.getOrderFieldEdit(order, field);
      const confirmedValue = edit.confirmed;
      const value = String(edit.draft ?? '').trim();
      edit.draft = value;

      if (value === confirmedValue) {
        return;
      }

      this.savingOrderFields[key] = true;
      this.serverError = null;

      try {
        const response = await axios.post(`/api/order/field/${order.id}/`, {
          field,
          value,
        });

        if (response.data.status === 'error') {
          const messages = (response.data.errors || []).map(error => translateApiError(error, this.t));
          throw new Error(messages.join(' ') || this.t('errors.serverError'));
        }

        const result = response.data.data || {};
        const savedValue = String(result.value ?? '');
        const currentOrder = (Array.isArray(this.orders) ? this.orders : [])
            .find(item => String(item.id) === String(order.id));
        if (currentOrder) {
          const currentEdit = this.getOrderFieldEdit(currentOrder, field);
          currentOrder[field] = savedValue;
          if (result.update_date) {
            currentOrder.update_date = result.update_date;
          }
          currentEdit.confirmed = savedValue;
          currentEdit.draft = savedValue;
        }
        this.addNotify({text: this.t('notifications.orderIdentifierSaveSuccess')});
      } catch (error) {
        const currentOrder = (Array.isArray(this.orders) ? this.orders : [])
            .find(item => String(item.id) === String(order.id));
        if (currentOrder) {
          const currentEdit = this.getOrderFieldEdit(currentOrder, field);
          currentEdit.draft = currentEdit.confirmed;
          currentOrder[field] = currentEdit.confirmed;
        } else {
          edit.draft = confirmedValue;
        }
        const message = error?.message || this.t('errors.serverError');
        this.serverError = message;
        this.addNotify({
          text: this.t('notifications.orderIdentifierSaveError', { error: message }),
          error: true,
        });
      } finally {
        delete this.savingOrderFields[key];
      }
    },

    getFiltersStorageKey(routeName = this.$route.name) {
      if (!ORDER_LIST_ROUTES.includes(routeName)) {
        return null;
      }

      return `${ORDER_FILTERS_STORAGE_PREFIX}:${routeName}`;
    },

    saveFiltersState(routeName = this.$route.name) {
      const storageKey = this.getFiltersStorageKey(routeName);

      if (!storageKey) {
        return;
      }

      const state = {
        filtersState: {
          selectedStatuses: [...this.filtersState.selectedStatuses],
          selectedSuppliers: [...this.filtersState.selectedSuppliers],
          selectedManagers: [...this.filtersState.selectedManagers],
          selectedOffices: [...this.filtersState.selectedOffices],
          showRefusalPositionsOnly: this.filtersState.showRefusalPositionsOnly,
          invoiceOrderSearch: this.filtersState.invoiceOrderSearch,
        },
        orderDateFilterMode: this.orderDateFilterMode,
        orderDate: this.orderDate,
        orderDateRange: this.orderDateRange,
      };

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.warn('Не удалось сохранить фильтры заказов:', error);
      }
    },

    restoreFiltersState(routeName = this.$route.name) {
      const storageKey = this.getFiltersStorageKey(routeName);

      if (!storageKey) {
        return;
      }

      let savedState;

      try {
        const rawState = sessionStorage.getItem(storageKey);
        if (!rawState) {
          this.resetFiltersState();
          return;
        }
        savedState = JSON.parse(rawState);
      } catch (error) {
        console.warn('Не удалось восстановить фильтры заказов:', error);
        this.resetFiltersState();
        return;
      }

      const savedFilters = savedState?.filtersState ?? {};
      const defaults = createDefaultFiltersState();

      this.filtersState = {
        selectedStatuses: Array.isArray(savedFilters.selectedStatuses) ? [...savedFilters.selectedStatuses] : defaults.selectedStatuses,
        selectedSuppliers: Array.isArray(savedFilters.selectedSuppliers) ? [...savedFilters.selectedSuppliers] : defaults.selectedSuppliers,
        selectedManagers: Array.isArray(savedFilters.selectedManagers) ? [...savedFilters.selectedManagers] : defaults.selectedManagers,
        selectedOffices: Array.isArray(savedFilters.selectedOffices) ? [...savedFilters.selectedOffices] : defaults.selectedOffices,
        showRefusalPositionsOnly: typeof savedFilters.showRefusalPositionsOnly === 'boolean'
            ? savedFilters.showRefusalPositionsOnly
            : defaults.showRefusalPositionsOnly,
        invoiceOrderSearch: typeof savedFilters.invoiceOrderSearch === 'string'
            ? savedFilters.invoiceOrderSearch
            : defaults.invoiceOrderSearch,
      };

      this.orderDateFilterMode = savedState?.orderDateFilterMode === 'single' ? 'single' : 'range';
      this.orderDate = this.restoreDate(savedState?.orderDate);
      this.orderDateRange = Array.isArray(savedState?.orderDateRange)
          ? savedState.orderDateRange.slice(0, 2).map(value => this.restoreDate(value))
          : null;
    },

    resetFiltersState() {
      this.filtersState = createDefaultFiltersState();
      this.orderDateFilterMode = 'range';
      this.orderDate = null;
      this.orderDateRange = null;
    },

    restoreDate(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    toggleCompletedOrders(event) {
      const routeName = event.target.checked ? 'orders-completed' : 'main';
      this.$router.push({ name: routeName });
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }

      this.sortKey = key;
      this.sortDir = 'asc';
    },

    normalizeSearch(value) {
      return String(value ?? '').trim().toLowerCase();
    },

    buildUniqueOptions(source, getter, labelGetter = value => String(value)) {
      const unique = new Map();

      (source || []).forEach(item => {
        const value = getter(item);
        if (value === null || value === undefined || value === '') return;

        if (!unique.has(value)) {
          unique.set(value, {
            value,
            label: labelGetter(value, item),
          });
        }
      });

      return Array.from(unique.values());
    },

    statusLabel(status) {
      return translateOrderStatus(status, this.t, this.isSupplier());
    },

    isDraftOrder(status) {
      return isOrderStatus(status, 'new');
    },

    isFinishedOrder(status) {
      return isOrderStatus(status, 'finish');
    },

    canFinishOrder(order) {
      return (this.user?.role === 'manager' || this.user?.role === 'admin') && !this.isFinishedOrder(order?.status);
    },

    openFinishConfirm(order) {
      this.finishOrderTarget = order;
      this.showFinishConfirm = true;
    },

    closeFinishConfirm() {
      if (this.isSubmitting === 'finishListOrder') {
        return;
      }

      this.showFinishConfirm = false;
      this.finishOrderTarget = null;
    },

    async finishSelectedOrder() {
      if (!this.finishOrderTarget?.id || this.isSubmitting === 'finishListOrder') {
        return;
      }

      this.isSubmitting = 'finishListOrder';
      this.serverError = null;

      try {
        const response = await axios.post(`/api/order/finish/${this.finishOrderTarget.id}/`, {}, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status == 'error') {
          this.serverError = '';
          for (let i in response.data.errors) {
            if (typeof response.data.errors[i].code == 'number') {
              this.serverError = this.t('errors.serverError');
              break;
            } else {
              this.serverError += translateApiError(response.data.errors[i], this.t) + '<br>';
            }
          }
          throw new Error(this.serverError);
        }

        this.finishOrderTarget.status = ORDER_STATUS_VALUES.finish;
        this.addNotify({text: this.t('orderPage.notify.finished')});
        this.showFinishConfirm = false;
        this.finishOrderTarget = null;
        await this.loadOrders();
      } catch (error) {
        this.serverError = error;
      } finally {
        this.isSubmitting = false;
      }
    },

    managerName(order) {
      return `${order?.user_last_name || ''} ${order?.user_name || ''}`.trim();
    },

    displayText(value) {
      return localizedDisplayValue(value, this.displayLocale());
    },

    displayEntityText(value, valueEn) {
      return localizedEntityValue(value, valueEn, this.displayLocale());
    },

    displayLocale() {
      return this.isSupplier() ? 'en' : this.locale;
    },

    isSupplier() {
      return this.user?.role === 'supplier';
    },

  },
};
</script>

<style scoped>
.order-identifier-input {
  box-sizing: border-box;
  display: block;
  max-width: 100%;
  min-width: 0;
  width: 100%;
}
</style>
